import type { Campaign, HistoryEntry } from './supabase';

/**
 * Der Freigabe-Ablauf in Klartext — ein Ort für Namen, Töne und die
 * Frage „wer ist dran?". Vorbild: status.ts im Event Manager.
 *
 * Die Datenbank kennt zwei Angaben: status (Versandstand) und review_state
 * (Freigabe). Für Menschen zählt eine einzige: der Stand.
 */
export type Stand =
  | 'entwurf' | 'rueckfrage' | 'eingereicht' | 'in_pruefung' | 'freigegeben'
  | 'geplant' | 'wird_versendet' | 'versendet' | 'abgelehnt' | 'fehler' | 'archiviert';

type Meta = { label: string; badge: string; wer: string; text: string };

export const STAND_META: Record<Stand, Meta> = {
  entwurf:        { label: 'Entwurf',          badge: 'badge-entwurf',     wer: 'Du',        text: 'Noch nicht eingereicht. Nur Du siehst die Mail.' },
  rueckfrage:     { label: 'Rückfrage',        badge: 'badge-rueckfrage',  wer: 'Du',        text: 'Die Redaktion hat eine Frage. Bitte anpassen und erneut einreichen.' },
  eingereicht:    { label: 'Eingereicht',      badge: 'badge-eingereicht', wer: 'Redaktion', text: 'Wartet auf die Redaktion. Ändern geht nur nach Zurückziehen.' },
  in_pruefung:    { label: 'In Prüfung',       badge: 'badge-in_pruefung', wer: 'Redaktion', text: 'Die Redaktion schaut gerade drauf.' },
  freigegeben:    { label: 'Freigegeben',      badge: 'badge-freigegeben', wer: 'Redaktion', text: 'Freigegeben — der Versand wird gerade vorbereitet.' },
  geplant:        { label: 'Geplant',          badge: 'badge-geplant',     wer: '—',         text: 'Freigegeben und für den Versand eingeplant.' },
  wird_versendet: { label: 'Wird versendet',   badge: 'badge-in_pruefung', wer: '—',         text: 'Brevo verschickt die Mail gerade.' },
  versendet:      { label: 'Versendet',        badge: 'badge-versendet',   wer: '—',         text: 'Die Mail ist raus.' },
  abgelehnt:      { label: 'Abgelehnt',        badge: 'badge-abgelehnt',   wer: '—',         text: 'Die Redaktion hat die Mail abgelehnt.' },
  fehler:         { label: 'Fehler beim Versand', badge: 'badge-fehler',   wer: 'Redaktion', text: 'Beim Versand ist etwas schiefgegangen. Die Redaktion kümmert sich.' },
  archiviert:     { label: 'Archiviert',       badge: 'badge-muted',       wer: '—',         text: 'Abgelegt.' },
};

/** Dieselben Stände aus Sicht der Redaktion (wo der Satz sonst „Du" meint). */
export const STAND_TEXT_REDAKTION: Partial<Record<Stand, string>> = {
  entwurf: 'Noch nicht eingereicht — die Person arbeitet noch daran.',
  rueckfrage: 'Rückfrage gestellt. Die Person passt die Mail an und reicht sie erneut ein.',
  eingereicht: 'Neu eingereicht — noch hat niemand aus der Redaktion sie in Prüfung genommen.',
  in_pruefung: 'In Prüfung. Als Nächstes: freigeben, zurückfragen oder ablehnen.',
  freigegeben: 'Freigegeben — der Versand ist noch nicht eingeplant.',
};

export function standVon(c: Pick<Campaign, 'status' | 'review_state'>): Stand {
  switch (c.status) {
    case 'sent': return 'versendet';
    case 'sending': return 'wird_versendet';
    case 'scheduled': return 'geplant';
    case 'failed': return 'fehler';
    case 'archived': return c.review_state === 'abgelehnt' ? 'abgelehnt' : 'archiviert';
    case 'pending_approval':
      if (c.review_state === 'freigegeben') return 'freigegeben';
      if (c.review_state === 'in_pruefung') return 'in_pruefung';
      return 'eingereicht';
    default:
      return c.review_state === 'rueckfrage' ? 'rueckfrage' : 'entwurf';
  }
}

/** Wartet die Mail auf die Redaktion? (zählt für „Freigaben" in der Seitenleiste) */
export function wartetAufRedaktion(c: Pick<Campaign, 'status' | 'review_state'>): boolean {
  const s = standVon(c);
  return s === 'eingereicht' || s === 'in_pruefung';
}

/** Verlaufseinträge in Klartext. */
export const AKTION_TEXT: Record<string, string> = {
  angelegt: 'Entwurf angelegt',
  eingereicht: 'Zur Freigabe eingereicht',
  erneut_eingereicht: 'Nach der Rückfrage erneut eingereicht',
  zurueckgezogen: 'Zurückgezogen, um sie zu ändern',
  in_pruefung: 'Von der Redaktion in Prüfung genommen',
  rueckfrage: 'Rückfrage der Redaktion',
  freigegeben: 'Freigegeben',
  selbst_versendet: 'Selbst verschickt — darf ohne Freigabe versenden',
  abgelehnt: 'Abgelehnt',
  versand_gestartet: 'Versand gestartet',
  geplant: 'Für den Versand eingeplant',
  versendet: 'Versendet',
  fehler: 'Fehler beim Versand',
  archiviert: 'Archiviert',
  entwurf: 'Zurück auf Entwurf',
  geloescht: 'Unter „Geplante Mails“ gelöscht',
  wiederhergestellt: 'Wiederhergestellt',
  event_zugeordnet: 'Einem Event im Event Manager zugeordnet',
  event_geloest: 'Vom Event im Event Manager gelöst',
};

export function aktionText(h: HistoryEntry): string {
  return AKTION_TEXT[h.action] ?? h.action;
}

/** Tage, die eine Mail schon auf die Redaktion wartet. */
export function wartetTage(c: Pick<Campaign, 'submitted_for_approval_at'>): number | null {
  if (!c.submitted_for_approval_at) return null;
  return Math.floor((Date.now() - new Date(c.submitted_for_approval_at).getTime()) / 86_400_000);
}

/**
 * Vorlauf: Die Redaktion sichtet montags bis freitags und braucht dafür vier
 * Werktage — am Montag eingereicht, geht die Mail frühestens am Freitag raus.
 * Das Wochenende zählt nicht mit. Die Datenbank prüft dieselbe Regel beim
 * Einreichen (mail_fruehester_versandtag).
 */
export const VORLAUF_WERKTAGE = 4;

/**
 * „eilt": Bis 10 Uhr an einem Werktag bei der Redaktion, geht die Mail noch am
 * selben Tag raus — sonst am nächsten Werktag. Ohne Begründung kein Versand.
 */
export const EILT_BIS_STUNDE = 10;
/** Mindestlänge der Begründung: ein ganzer Satz statt „dringend!". */
export const EILT_GRUND_MIN_ZEICHEN = 30;
export const EILT_GRUND_MIN_WOERTER = 4;

export function istWerktag(d: Date): boolean {
  const w = d.getDay();
  return w !== 0 && w !== 6;
}

/** „2026-10-02" (Ortszeit) */
export function isoTag(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Frühester Versandtag für eine Mail, die zu `eingang` bei der Redaktion ist:
 * ohne „eilt" der vierte Werktag danach, mit „eilt" derselbe Werktag (bis
 * 10 Uhr) oder der nächste.
 */
export function fruehesterVersandtag(eingang: Date = new Date(), eilt = false): Date {
  const d = new Date(eingang);
  d.setHours(0, 0, 0, 0);
  if (eilt) {
    if (istWerktag(d) && eingang.getHours() < EILT_BIS_STUNDE) return d;
    do d.setDate(d.getDate() + 1); while (!istWerktag(d));
    return d;
  }
  let n = 0;
  while (n < VORLAUF_WERKTAGE) {
    d.setDate(d.getDate() + 1);
    if (istWerktag(d)) n++;
  }
  return d;
}

/** Was an der Begründung für „eilt" noch fehlt — oder null, wenn sie reicht. */
export function eiltGrundFehler(grund: string | null | undefined): string | null {
  const t = (grund ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return 'Schreib dazu, warum es eilt — ohne Begründung geht die Mail nicht raus.';
  const woerter = t.split(' ').filter((w) => /[\p{L}\p{N}]/u.test(w)).length;
  if (t.length < EILT_GRUND_MIN_ZEICHEN || woerter < EILT_GRUND_MIN_WOERTER) {
    return `Die Begründung ist zu knapp — mindestens ${EILT_GRUND_MIN_ZEICHEN} Zeichen und ${EILT_GRUND_MIN_WOERTER} Wörter: Was ist passiert, und warum kann es nicht warten?`;
  }
  if (/(.)\1{5,}/u.test(t)) return 'Bitte eine echte Begründung schreiben.';
  return null;
}

/** Wann die aktuelle Prüfrunde begann: das letzte „eingereicht" (nach einer Rückfrage läuft die Frist weiter). */
export function eingangAus(verlauf: HistoryEntry[]): Date | null {
  const e = verlauf.filter((h) => h.action === 'eingereicht').map((h) => new Date(h.created_at).getTime());
  return e.length ? new Date(Math.max(...e)) : null;
}

export function werktageBis(ziel: Date, von: Date = new Date()): number {
  let tage = 0;
  const d = new Date(von);
  d.setHours(0, 0, 0, 0);
  const z = new Date(ziel);
  z.setHours(0, 0, 0, 0);
  while (d < z) {
    d.setDate(d.getDate() + 1);
    const wt = d.getDay();
    if (wt !== 0 && wt !== 6) tage++;
  }
  return tage;
}
